import { createStore, SetState } from '@oneappexchange/lwc-wired';
import { ValueOf } from 'ts-essentials';
import { IS_DEVELOPMENT } from '~/modules/config';
import { I18nState, i18nStore, selectLocationLabels } from '~/modules/stores/i18nStore';
import { logger } from '~/modules/utilities/logger';

export const VisibilityStatus = Object.freeze({
    HIDDEN: 'HIDDEN',
    VISIBLE: 'VISIBLE',
    LOADING: 'LOADING',
});
export type VisibilityStatusType = ValueOf<typeof VisibilityStatus>;

export type LocationOption = {
    value: string;
    label: string;
};

export interface LeadFormData {
    firstName: string;
    lastName: string;
    company: string;
    country: string;
    state: string;
    email: string;
    additionalDetails: string;
    optInToBeContacted: boolean;
}

export interface LeadCollectionState {
    visibilityStatus: VisibilityStatusType;
    readOnly: boolean;
    countryOptions: LocationOption[];
    stateOptions: LocationOption[];
    formData: LeadFormData;
}

export const initialState: LeadCollectionState = {
    visibilityStatus: VisibilityStatus.VISIBLE,
    readOnly: false,
    countryOptions: buildLocationOptions(),
    stateOptions: [],
    formData: {
        firstName: '',
        lastName: '',
        company: '',
        country: '',
        state: '',
        email: '',
        additionalDetails: '',
        optInToBeContacted: false,
    },
};

export const leadCollectionStore = createStore(
    initialState,
    (set: SetState<LeadCollectionState>, get: GetState<LeadCollectionState>) => ({
        setVisibilityStatus(visibilityStatus: VisibilityStatusType) {
            set(function setVisibilityStatus(draft) {
                draft.visibilityStatus = visibilityStatus;
            });
        },
        setReadOnly(
            readOnly: boolean,
            options: {
                overrideFormData?: Partial<LeadCollectionState['formData']>;
                overrideCountryOptions?: LeadCollectionState['countryOptions'];
                overrideStateOptions?: LeadCollectionState['stateOptions'];
            } = {},
        ) {
            set(function setReadOnly(draft) {
                draft.readOnly = readOnly;
                if (options.overrideFormData) {
                    Object.entries(options.overrideFormData).forEach(([key, value]) => {
                        (draft.formData as AnyObject)[key] = value;
                    });
                }
                if (options.overrideCountryOptions) {
                    draft.countryOptions = options.overrideCountryOptions;
                }
                if (options.overrideStateOptions) {
                    draft.stateOptions = options.overrideStateOptions;
                }
            });
        },
        setFormDataField(field: keyof LeadCollectionState['formData'], value: unknown) {
            const formDataFields = Object.keys(initialState.formData);
            if (!formDataFields.includes(field)) {
                logger.error(
                    `Attempted to set invalid field '${field}' in formData. Valid values are: ${JSON.stringify(
                        formDataFields,
                    )}`,
                );
                return;
            }
            const newType = typeof value;
            const typeOfExistingField = typeof (get().formData as unknown as AnyObject)[field];
            if (typeof (get().formData as unknown as AnyObject)[field] !== typeof value) {
                logger.error(
                    `Attempted to set field '${field}' in formData to a type different type (${newType}) than what was expected (${typeOfExistingField}).`,
                );
                return;
            }
            set(function setFormDataField(draft) {
                const previousValue = (draft.formData as AnyObject)[field];
                (draft.formData as AnyObject)[field] = value;

                // When users change the country, the state needs to be reset as well
                if (field === 'country' && value !== previousValue) {
                    draft.formData.state = '';
                    draft.stateOptions = buildStateOptions(draft.formData.country);
                }
            });
        },
    }),
    'leadCollectionStore',
    IS_DEVELOPMENT,
);

function selectCountryAndStateOptions(state: I18nState) {
    return {
        countryOptions: buildLocationOptions(selectLocationLabels(state.i18nStaticCache).countries),
        stateOptions: buildStateOptions(leadCollectionStore.getState().formData.country),
    };
}
function i18nStoreCallback(partialState: ReturnType<typeof selectCountryAndStateOptions>) {
    leadCollectionStore.setState(partialState);
}
i18nStore.subscribe(i18nStoreCallback, selectCountryAndStateOptions);

export function selectLeadCollectionFormWithLocations(
    i18nStaticCache: I18nMessages = i18nStore.getState().i18nStaticCache,
) {
    return {
        locations: i18nStaticCache.location,
        leadCollectionForm: i18nStaticCache.leadCollectionForm,
        shared: i18nStaticCache.shared,
        termsAndConditions: i18nStaticCache.termsAndConditions,
    };
}

export function buildLocationOptions(
    locations: Record<string, string> = selectLocationLabels().countries,
): LocationOption[] {
    const options = Object.keys(locations).map((key) => ({ value: locations[key], label: locations[key] }));
    return options;
}

export function buildStateOptions(
    country: LeadCollectionState['formData']['country'] = leadCollectionStore.getState().formData.country,
): LocationOption[] {
    const { locations } = selectLeadCollectionFormWithLocations();
    switch (country) {
        case locations.countries.US:
            return buildLocationOptions(locations.usStates);
        case locations.countries.CA:
            return buildLocationOptions(locations.caStates);
        case locations.countries.JP:
            return buildLocationOptions(locations.jpStates);
        case locations.countries.AU:
            return buildLocationOptions(locations.auStates);
        case locations.countries.BR:
            return buildLocationOptions(locations.brStates);
        case locations.countries.CN:
            return buildLocationOptions(locations.cnStates);
        case locations.countries.IN:
            return buildLocationOptions(locations.inStates);
        default:
            return [];
    }
}
